// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Persistent banner shown while a superadmin is acting as another user.
 *
 * Without it, an impersonated session is visually indistinguishable from a
 * real login, so an admin can forget they are impersonating and take actions
 * that look — to anyone reading the audit log — like the user took them.
 *
 * The signal comes from the `impersonated` claim the backend embeds in the
 * access token (see backend/internal/pkg/token/jwt.go). Reading it here is for
 * display only; the backend independently enforces the session's short TTL and
 * records `impersonated_by` on every audit event, so a tampered claim changes
 * nothing but this banner.
 */

import { useMemo } from 'react';
import { ShieldAlert, LogOut } from 'lucide-react';
import { useAuthStore } from '@/app/stores/auth-store';
import { useLogout } from '@/app/features/auth/hooks/useAuth';

interface ImpersonationClaims {
  impersonated?: boolean;
  impersonator_id?: string;
}

/**
 * Decodes a JWT payload without verifying it. Safe here because the value is
 * only used to decide whether to render a warning — never to grant access.
 */
function readClaims(accessToken: string | null): ImpersonationClaims | null {
  if (!accessToken) return null;
  const payload = accessToken.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as ImpersonationClaims;
  } catch {
    return null;
  }
}

export function ImpersonationBanner() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();

  const claims = useMemo(() => readClaims(accessToken), [accessToken]);

  if (!claims?.impersonated) return null;

  return (
    <div
      role="status"
      className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-[14px] text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      <ShieldAlert className="h-4 w-4 shrink-0" />
      <p className="flex-1">
        You are viewing FlairCV as{' '}
        <span className="font-semibold">{user?.email ?? 'this user'}</span>.
        Actions you take are recorded against their account.
      </p>
      <button
        type="button"
        onClick={() => logout.mutate()}
        disabled={logout.isPending}
        className="inline-flex items-center gap-1.5 rounded-md border border-amber-400 px-3 py-1.5 text-[13px] font-medium transition-colors hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:pointer-events-none disabled:opacity-50 dark:border-amber-600 dark:hover:bg-amber-900"
      >
        <LogOut className="h-3.5 w-3.5" />
        End session
      </button>
    </div>
  );
}
